import { BodySecondary, LabelMini } from '@liveauctioneers/hammer-ui-core/text';
import { FirstTimeWalkthroughSteps } from '@/types/walkthroughs';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidOptions } from '@/redux/modules/bidOptions';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { Lock } from '@liveauctioneers/hammer-ui-core/icons';
import { useAppSelector } from '@/redux/hooks';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import isEqual from 'react-fast-compare';
import ItemPriceGatewayContainer from '@/components/ItemPriceGateway/ItemPriceGatewayContainer';
import styled from 'styled-components';

export interface ItemBiddingStatusRowProps {
    itemId: number;
}

const ItemBiddingStatusRow = (props: ItemBiddingStatusRowProps) => {
    const { itemId } = props;

    const {
        bidActivityCount = 0,
        bidCount = 0,
        currency = 'usd',
        isCatalogOnly,
        isLocked = false,
        isPassed = false,
        isSold = false,
        leadingBid = 0,
        salePrice = 0,
        saleStartTs,
        startPrice = 1,
    } = useAppSelector((state) => getItemSummary(state, itemId));
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const bidOptions = useAppSelector((state) => getBidOptions(state, itemId), isEqual);
    const auctionRunning = Date.now() > saleStartTs * 1000 && !isSold && !isPassed && !isCatalogOnly;

    const firstBidOption = bidOptions?.[0] || 0;
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    // just need to show the start price if neither of the other options are available
    const askPrice = leadingBid || firstBidOption || startPrice;

    let lotEndPrice = null;

    if (isUnsold) {
        lotEndPrice = <FormattedMessage id="lot_unsold_pending" />;
    } else if (isSold) {
        lotEndPrice = salePrice ? (
            <FormattedMessage
                id="sold_for"
                values={{
                    amount: (
                        <StyledSoldForCurrency
                            conversionH3
                            convert
                            noFraction
                            sourceCurrency={currency}
                            value={salePrice}
                        />
                    ),
                }}
            />
        ) : (
            <FormattedMessage id="sold" />
        );
    } else if (isPassed) {
        lotEndPrice = <FormattedMessage id="itemBidding.passedText" />;
    }

    const priceText = (
        <StyledPrice className="hui-text-h1">
            <FormattedCurrency
                conversionH3
                convert
                data-walkthrough-step={auctionRunning ? undefined : FirstTimeWalkthroughSteps.FutureItem}
                noFraction
                sourceCurrency={currency}
                value={askPrice}
            />
        </StyledPrice>
    );

    const bidCountValue = Math.max(bidActivityCount, bidCount);

    const countLabel = (
        <BodySecondary color="secondary">
            {bidCountValue > 0 ? (
                <FormattedMessage
                    id="itemBidding.currentPrice"
                    values={{ bidCount: bidCountValue }}
                />
            ) : (
                // no bids, show Starting Bid text
                <FormattedMessage id="itemBidding.startingPrice" />
            )}
        </BodySecondary>
    );

    if (isCatalogOnly) {
        return null;
    }

    return (
        <div>
            <StyledStatusLabel>
                {isPassed || isSold || isUnsold ? (
                    <StyledPriceGatewayContainer
                        itemId={itemId}
                        priceGatewayIcon
                    >
                        <StyledPrice className="hui-text-h1">{lotEndPrice}</StyledPrice>
                    </StyledPriceGatewayContainer>
                ) : (
                    <>
                        <StyledContentWrapper>
                            {countLabel}
                            <SecureLabel>
                                <Lock
                                    color="primary"
                                    size="xs"
                                    style="fill"
                                />
                                <LabelMini color="secondary">
                                    &nbsp;
                                    <FormattedMessage id="secure_bidding" />
                                </LabelMini>
                            </SecureLabel>
                        </StyledContentWrapper>
                        {priceText}
                    </>
                )}
            </StyledStatusLabel>

            {isPassed && userLoggedIn && (
                <StyledPassed>
                    <FormattedMessage
                        id="starting_bid_lot_passed"
                        values={{
                            amount: (
                                <FormattedCurrency
                                    key="starting-bid-lot-passed-amount"
                                    noFraction
                                    sourceCurrency={currency}
                                    value={startPrice}
                                />
                            ),
                        }}
                    />
                </StyledPassed>
            )}
            {isUnsold && userLoggedIn && (
                <StyledPassed>
                    <FormattedMessage id="lot_unsold_pending" />
                </StyledPassed>
            )}
        </div>
    );
};

export default ItemBiddingStatusRow;

const StyledPriceGatewayContainer = styled(ItemPriceGatewayContainer)`
    margin-right: 24px;
`;

const StyledPassed = styled.div`
    margin-top: 5px;
    text-transform: uppercase;
`;

const StyledPrice = styled.span`
    margin-right: 10px;
    display: flex;
    align-items: center;

    @media print {
        text-align: center;
        font-size: unset;
        font-family: unset;
        flex-grow: 1;
    }
`;

const StyledStatusLabel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const StyledSoldForCurrency = styled(FormattedCurrency)`
    padding-left: 5px;
`;

const StyledContentWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const SecureLabel = styled.div`
    display: flex;
    align-items: center;
`;
